import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

function reveal(isVisible: boolean, extra = '') {
  return `fade-up ${isVisible ? 'revealed' : ''} ${extra}`.trim();
}

function stagger(i: number): React.CSSProperties {
  return { '--stagger': i } as React.CSSProperties;
}

export function MarketSection() {
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.05 });

  return (
    <section id="market" className="section market-section" aria-label="The Market Collapse" ref={ref}>
      <div className="section-inner">
        <h2 className={reveal(isVisible)}>The Market Collapse</h2>
        <p className={reveal(isVisible, 'section-subtitle')} style={stagger(1)}>Incumbents are failing. The opportunity is massive.</p>

        {/* Failure cards */}
        <div className="market-subsection">
          <h3 className={reveal(isVisible, 'market-heading')} style={stagger(2)}>Incumbents Are Failing</h3>
          <div className="failure-grid" data-stagger>
            <div className={reveal(isVisible, 'failure-card')}>
              <md-elevation></md-elevation>
              <span className="failure-name">Chegg</span>
              <span className="failure-stat">-99% stock</span>
              <span className="failure-detail">From $14.7B to ~$71M market cap. AI destroyed the homework-answer model overnight.</span>
            </div>
            <div className={reveal(isVisible, 'failure-card')}>
              <md-elevation></md-elevation>
              <span className="failure-name">BYJU&apos;S</span>
              <span className="failure-stat">$22B &rarr; $0</span>
              <span className="failure-detail">Once the world&apos;s most-valued edtech unicorn. Now bankrupt. Content delivery without learning science.</span>
            </div>
            <div className={reveal(isVisible, 'failure-card')}>
              <md-elevation></md-elevation>
              <span className="failure-name">Coursera + Udemy</span>
              <span className="failure-stat">Merger</span>
              <span className="failure-detail">Two public companies forced to merge. MOOC completion rates remain 5-15%. Passive video is failing.</span>
            </div>
            <div className={reveal(isVisible, 'failure-card')}>
              <md-elevation></md-elevation>
              <span className="failure-name">GenAI Pilots</span>
              <span className="failure-stat">95% fail</span>
              <span className="failure-detail">Enterprises throwing GPT at training without learning science. Garbage in, garbage out.</span>
            </div>
          </div>
        </div>

        {/* Competitor Comparison Grid */}
        <div className="market-subsection">
          <h3 className={reveal(isVisible, 'market-heading')}>Competitive Comparison</h3>
          <div className={reveal(isVisible, 'competitor-grid-wrapper')}>
            <div className="competitor-grid">
              <table>
                <thead>
                  <tr>
                    <th>Competitor</th>
                    <th>Active Learning</th>
                    <th>Frontier AI</th>
                    <th>Human Connection</th>
                    <th>Process Assessment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="competitor-name">Duolingo (~$5.2B)</td>
                    <td>Gamification only</td>
                    <td>Content AI</td>
                    <td>None</td>
                    <td>Answer-based</td>
                  </tr>
                  <tr>
                    <td className="competitor-name">Khanmigo</td>
                    <td>Partial</td>
                    <td>Generic GPT</td>
                    <td>None</td>
                    <td>Quiz-based</td>
                  </tr>
                  <tr>
                    <td className="competitor-name">Sana Labs ($1.1B)</td>
                    <td>None</td>
                    <td>Adaptive paths</td>
                    <td>None</td>
                    <td>Completion-based</td>
                  </tr>
                  <tr>
                    <td className="competitor-name">Google Guided Learning</td>
                    <td>None</td>
                    <td>Gemini (feature)</td>
                    <td>None</td>
                    <td>None</td>
                  </tr>
                  <tr>
                    <td className="competitor-name">OpenAI Study Mode</td>
                    <td>None</td>
                    <td>GPT-4</td>
                    <td>None</td>
                    <td>None</td>
                  </tr>
                  <tr>
                    <td className="competitor-name">Coursera + Udemy</td>
                    <td>Passive video</td>
                    <td>None</td>
                    <td>Forum only</td>
                    <td>Certificate</td>
                  </tr>
                  <tr>
                    <td className="competitor-name">NotebookLM (Google)</td>
                    <td>Opt-in guide</td>
                    <td>Gemini (feature)</td>
                    <td>None</td>
                    <td>Quiz-based</td>
                  </tr>
                  <tr className="competitor-highlight">
                    <td className="competitor-name"><strong>Eduksy</strong></td>
                    <td><strong>Full active</strong></td>
                    <td><strong>Gemini (core)</strong></td>
                    <td><strong>Mentors + Peers</strong></td>
                    <td><strong>Process-based</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Opportunity cards */}
        <div className="market-subsection">
          <h3 className={reveal(isVisible, 'market-heading')}>The Opportunity</h3>
          <div className="opportunity-grid" data-stagger>
            <div className={reveal(isVisible, 'opportunity-card')}>
              <md-elevation></md-elevation>
              <span className="material-symbols-outlined opp-icon">smart_toy</span>
              <span className="opp-stat">$1.63B &rarr; $7.99B</span>
              <span className="opp-label">AI Tutoring Market</span>
              <span className="opp-detail">CAGR 25.4%. The market is screaming for intelligent, science-backed learning tools.</span>
            </div>
            <div className={reveal(isVisible, 'opportunity-card')}>
              <md-elevation></md-elevation>
              <span className="material-symbols-outlined opp-icon">corporate_fare</span>
              <span className="opp-stat">$350–370B</span>
              <span className="opp-label">Corporate Training</span>
              <span className="opp-detail">Adults learn through self-direction and problem-solving, not passive lectures. Most corporate training still uses pedagogy on adults. $350B spent with almost zero measurement.</span>
            </div>
            <div className={reveal(isVisible, 'opportunity-card')}>
              <md-elevation></md-elevation>
              <span className="material-symbols-outlined opp-icon">trending_up</span>
              <span className="opp-stat">$1.1B &amp; $4.8B</span>
              <span className="opp-label">Recent Exits</span>
              <span className="opp-detail">Sana Labs ($1.1B) and Instructure ($4.8B), proving the market rewards learning infrastructure.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
